import { useEffect, useMemo, useState } from "react";
import { list } from "../lib/pb";
import { navigate } from "../App";
import { useT } from "../lib/i18n";
import type { AuditEntry, AuditAction, Editor } from "../lib/types";

const ACTIONS: Array<AuditAction | "all"> = [
  "all",
  "article.create", "article.update", "article.publish",
  "article.unpublish", "article.delete",
  "tag.create", "tag.update", "tag.delete",
  "editor.login", "editor.update",
];
const PER_PAGE = 50;

export default function AuditLogPage() {
  const t = useT();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [editors, setEditors] = useState<Editor[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterAction, setFilterAction] = useState<AuditAction | "all">("all");
  const [filterActor, setFilterActor] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    list<Editor>("editors", { perPage: 100, sort: "name" })
      .then((r) => setEditors(r.items))
      .catch(() => setEditors([]));
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterActor, fromDate, toDate, page]);

  async function load() {
    setLoading(true);
    try {
      const parts: string[] = [];
      if (filterAction !== "all") parts.push(`action='${filterAction}'`);
      if (filterActor !== "all") parts.push(`actor='${filterActor}'`);
      if (fromDate) parts.push(`created >= '${fromDate} 00:00:00'`);
      if (toDate) parts.push(`created <= '${toDate} 23:59:59'`);
      const r = await list<AuditEntry>("audit_log", {
        page,
        perPage: PER_PAGE,
        sort: "-created",
        filter: parts.length ? parts.join(" && ") : undefined,
        expand: "actor",
      });
      setItems(r.items);
      setTotalPages(r.totalPages);
    } catch (e) {
      alert(`Failed to load audit log: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetFilters() {
    setFilterAction("all");
    setFilterActor("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  }

  const hasFilters = useMemo(
    () => filterAction !== "all" || filterActor !== "all" || fromDate || toDate,
    [filterAction, filterActor, fromDate, toDate],
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.auditTitle}</h1>
        <p className="page-subtitle">{t.auditSubtitle}</p>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: 14, marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ minWidth: 180 }}>
          <span className="field-label">{t.auditFilterAction}</span>
          <select
            value={filterAction}
            onChange={(e) => { setPage(1); setFilterAction(e.target.value as AuditAction | "all"); }}
            className="select"
          >
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <span className="field-label">{t.auditFilterActor}</span>
          <select
            value={filterActor}
            onChange={(e) => { setPage(1); setFilterActor(e.target.value); }}
            className="select"
          >
            <option value="all">{t.auditAllActors}</option>
            {editors.map((ed) => (
              <option key={ed.id} value={ed.id}>{ed.name} — {ed.email}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <span className="field-label">{t.auditFilterFrom}</span>
          <input type="date" value={fromDate} onChange={(e) => { setPage(1); setFromDate(e.target.value); }} className="input" />
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <span className="field-label">{t.auditFilterTo}</span>
          <input type="date" value={toDate} onChange={(e) => { setPage(1); setToDate(e.target.value); }} className="input" />
        </div>
        {hasFilters && (
          <button onClick={resetFilters} className="btn btn-ghost btn-sm" style={{ marginBottom: 1 }}>
            {t.auditReset}
          </button>
        )}
      </div>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : items.length === 0 ? (
        <div className="empty">{t.auditEmpty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((e) => {
            const isOpen = expanded.has(e.id);
            const hasDetails = e.details && Object.keys(e.details).length > 0;
            const targetClickable = e.target_collection === "articles" && !e.action.endsWith(".delete");
            return (
              <div key={e.id} className="card" style={{ overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                  <span className="mono" style={{ fontSize: 11, color: "var(--text-3)", width: 140, flexShrink: 0 }}>
                    {formatDate(e.created)}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-1)", width: 140, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.expand?.actor?.name || e.actor || "system"}
                  </span>
                  <ActionBadge action={e.action} />
                  <span
                    style={{
                      fontSize: 12,
                      color: targetClickable ? "var(--accent)" : "var(--text-1)",
                      flex: 1,
                      cursor: targetClickable ? "pointer" : "default",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                    onClick={() => { if (targetClickable) navigate(`/article/${e.target_id}`); }}
                  >
                    {e.target_label || `${e.target_collection}/${e.target_id}`}
                  </span>
                  <button
                    onClick={() => toggleExpand(e.id)}
                    className="btn btn-ghost btn-sm"
                  >
                    {isOpen ? t.auditHideDetails : hasDetails ? t.auditViewDetails : t.auditShowDetails}
                  </button>
                </div>
                {isOpen && (
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "var(--bg-0)",
                      borderTop: "1px solid var(--border-1)",
                      fontSize: 11, fontFamily: "var(--font-mono)",
                    }}
                  >
                    {hasDetails ? <DetailsView details={e.details} /> : (
                      <div style={{ color: "var(--text-3)" }}>
                        {t.auditNoDiff}
                      </div>
                    )}
                    {e.ip && (
                      <div style={{ marginTop: 8, color: "var(--text-3)" }}>
                        IP: {e.ip}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 22, color: "var(--text-3)", fontSize: 12 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn btn-secondary btn-sm">
            ← Prev
          </button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="btn btn-secondary btn-sm">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function DetailsView({ details }: { details: Record<string, { from: unknown; to: unknown }> }) {
  const keys = Object.keys(details);
  if (keys.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {keys.map((k) => {
        const d = details[k];
        return (
          <div key={k} style={{ display: "flex", gap: 12, lineHeight: 1.5, alignItems: "flex-start" }}>
            <span style={{ color: "var(--accent)", width: 160, flexShrink: 0 }}>{k}</span>
            <span style={{ flex: 1, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <code style={{ padding: "1px 6px", background: "rgba(224,82,82,0.16)", color: "#ff8a8a", borderRadius: 3, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {formatVal(d.from)}
              </code>
              <span style={{ color: "var(--text-3)" }}>→</span>
              <code style={{ padding: "1px 6px", background: "rgba(62,207,113,0.16)", color: "#7be0a0", borderRadius: 3, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {formatVal(d.to)}
              </code>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") {
    if (v.length === 0) return "(empty)";
    if (v.length > 120) return JSON.stringify(v.slice(0, 120) + "…");
    return JSON.stringify(v);
  }
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  try {
    const s = JSON.stringify(v, null, 2);
    return s.length > 200 ? s.slice(0, 200) + "…" : s;
  } catch {
    return String(v);
  }
}

function ActionBadge({ action }: { action: AuditAction }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    create:    { bg: "rgba(62,207,113,0.18)", fg: "var(--success)" },
    update:    { bg: "rgba(95,160,255,0.18)", fg: "#79b1ff" },
    publish:   { bg: "rgba(184,134,74,0.22)", fg: "var(--accent)" },
    unpublish: { bg: "rgba(142,142,147,0.18)", fg: "var(--text-2)" },
    delete:    { bg: "rgba(224,82,82,0.18)", fg: "var(--danger)" },
    login:     { bg: "rgba(72,72,74,0.5)", fg: "var(--text-3)" },
  };
  const verb = action.split(".")[1] || "update";
  const c = colors[verb] || colors.update;
  return (
    <span
      style={{
        padding: "2px 8px", borderRadius: 100,
        fontSize: 10, fontWeight: 600,
        letterSpacing: "0.06em", textTransform: "uppercase",
        background: c.bg, color: c.fg,
        whiteSpace: "nowrap", flexShrink: 0,
        width: 140, textAlign: "center",
      }}
    >
      {action}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
}
